/**
 * The terminal outcome a render subtree can report for a content key: the content painted
 * (`shown`) or its load failed and an error state rendered instead (`failed`). A scenario
 * waiting on the key maps these onto its status (`ok` / `failure`).
 */
export type ContentOutcome = 'shown' | 'failed';

type ContentKey = string;

interface ContentOutcomeSignalStore {
  readonly waiters: Map<ContentKey, (outcome: ContentOutcome) => void>;
  readonly outcomeBeforeWaiter: Map<ContentKey, ContentOutcome>;
}

const store: ContentOutcomeSignalStore = {
  waiters: new Map(),
  outcomeBeforeWaiter: new Map(),
};

/**
 * Upper bound on keys retained in `outcomeBeforeWaiter`. A key lands here only when an outcome
 * is reported before its consumer registers interest (the outcome-first race). Interactions are
 * sequential from a user's perspective, so only a couple can overlap; this leaves a wide safety
 * margin while preventing unbounded growth over a long-lived tab.
 */
const maxRetainedOutcomeBeforeWaiter = 50;

const rememberOutcomeBeforeWaiter = (key: ContentKey, outcome: ContentOutcome): void => {
  store.outcomeBeforeWaiter.set(key, outcome);

  if (store.outcomeBeforeWaiter.size > maxRetainedOutcomeBeforeWaiter) {
    const oldest = store.outcomeBeforeWaiter.keys().next().value;
    /* istanbul ignore else -- a non-empty Map always yields a first key; the guard only narrows `string | undefined` */
    if (oldest !== undefined) {
      store.outcomeBeforeWaiter.delete(oldest);
    }
  }
};

/**
 * Signals that the content identified by `key` reached a terminal `outcome`. If a consumer is
 * already waiting on this key it is resolved immediately; otherwise the outcome is briefly
 * retained so a consumer that registers shortly after (the outcome-first race) still resolves.
 */
export const reportContentOutcome = (key: ContentKey, outcome: ContentOutcome): void => {
  const onOutcome = store.waiters.get(key);

  if (onOutcome) {
    store.waiters.delete(key);
    onOutcome(outcome);

    return;
  }

  rememberOutcomeBeforeWaiter(key, outcome);
};

/**
 * Registers interest in the content identified by `key` and invokes `onOutcome` with the
 * terminal outcome when it is reported. Resolves synchronously if the outcome has already been
 * reported (the outcome-first race). Returns a cancel function the caller must invoke if it
 * stops caring (e.g. the scenario is abandoned or times out) so a never-resolved key can't leak
 * a waiter.
 *
 * One waiter per key: if a second consumer registers for a key that is still pending, the later
 * registration replaces the earlier one (last registration wins). The returned cancel only
 * removes the caller's own registration, so a stale cancel from a replaced waiter can't evict
 * the current one. Callers keep keys unique (e.g. per-thread telemetryIds, or a single in-flight
 * navigation), so this is a safety contract rather than an expected path.
 */
export const waitForContentOutcome = (key: ContentKey, onOutcome: (outcome: ContentOutcome) => void): (() => void) => {
  const bufferedOutcome = store.outcomeBeforeWaiter.get(key);
  if (bufferedOutcome !== undefined) {
    store.outcomeBeforeWaiter.delete(key);
    onOutcome(bufferedOutcome);

    return () => undefined;
  }

  store.waiters.set(key, onOutcome);

  return () => {
    if (store.waiters.get(key) === onOutcome) {
      store.waiters.delete(key);
    }
  };
};

/**
 * Signals that the content identified by `key` has painted. Called from the render side after
 * style and layout (see `reportContentShownAfterStyleAndLayout`).
 */
export const reportContentShown = (key: ContentKey): void => {
  reportContentOutcome(key, 'shown');
};

/**
 * Signals that the content identified by `key` failed to load and an error state rendered
 * instead. Reported immediately (a failure is not a paint), so no style-and-layout deferral.
 */
export const reportContentFailed = (key: ContentKey): void => {
  reportContentOutcome(key, 'failed');
};

/**
 * Shown-only convenience over {@link waitForContentOutcome} for consumers that only care about
 * the paint (e.g. `post_shown`, which sources failure from its publish lifecycle rather than the
 * render side). Ignores a `failed` outcome.
 */
export const waitForContentShown = (key: ContentKey, onShown: () => void): (() => void) =>
  waitForContentOutcome(key, (outcome) => {
    if (outcome === 'shown') {
      onShown();
    }
  });

/**
 * Clears all pending waiters and buffered outcomes. Test-only: scenarios keyed by a constant
 * (e.g. `open_home_feed`) share this module-level store across tests, so specs must reset it
 * between cases to stay isolated. Not exported from the package's public entry point.
 */
export const resetContentOutcomeSignalForTests = (): void => {
  store.waiters.clear();
  store.outcomeBeforeWaiter.clear();
};
